import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Event } from '@planner/types'
import { useSubscription } from '@/features/subscriptions/hooks/useSubscription'

export function useEvents() {
  const { user, userRole } = useAuth()
  const { canCreateEvent, subscription, features } = useSubscription()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const hasFetchedRef = useRef(false)

  const fetchEvents = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }

    // Don't fetch if already fetching
    if (isFetching) {
      return
    }

    // Don't fetch if userRole is null - wait for it to be loaded
    // This prevents fetching with incomplete auth state
    if (userRole === null) {
      return
    }

    // Check cache - if data was fetched recently (within 30 seconds), skip fetch
    const cacheKey = `events_cache_${user.id}_${userRole}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) {
      try {
        const { data: cachedData, timestamp } = JSON.parse(cached)
        const age = Date.now() - timestamp
        if (age < 30000 && cachedData && cachedData.length > 0) {
          setEvents(cachedData)
          setLoading(false)
          return
        }
      } catch (e) {
        // Invalid cache, continue with fetch
      }
    }

    setIsFetching(true)
    setError(null)

    try {
      // Create timeout promise (5 seconds)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('fetchEvents timeout'))
        }, 5000)
      })

      let query = supabase
        .from('events')
        .select(`
          *,
          clients!events_client_id_fkey (
            id,
            full_name,
            email
          ),
          clients_2:clients!events_client_id_2_fkey (
            id,
            full_name,
            email
          )
        `)
        .order('event_date', { ascending: false })

      // Filter by role
      if (userRole === 'klient') {
        if (!user.email) {
          setEvents([])
          setLoading(false)
          setIsFetching(false)
          return
        }

        // Add timeout to client lookup
        const clientLookupPromise = supabase
          .from('clients')
          .select('id')
          .eq('email', user.email)
          .single()

        const { data: clientData } = await Promise.race([
          clientLookupPromise,
          timeoutPromise
        ]) as any
        if (clientData && 'id' in clientData) {
          const clientId = (clientData as { id: string }).id
          query = query.eq('client_id', clientId)
        } else {
          setEvents([])
          setLoading(false)
          setIsFetching(false)
          return
        }
      }

      // Add timeout to main query
      const { data, error: fetchError } = await Promise.race([
        query,
        timeoutPromise
      ]) as any

      if (fetchError) {
        setError(fetchError.message)
        setLoading(false)
      } else {
        const eventsData = data || []
        setEvents(eventsData)
        setLoading(false)
        // Cache the data in sessionStorage
        const cacheKey = `events_cache_${user.id}_${userRole}`
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: eventsData,
            timestamp: Date.now()
          }))
        } catch (e) {
          // sessionStorage might be full or unavailable, ignore
        }
      }
    } catch (err) {
      // Handle timeout or other errors
      if (err instanceof Error && err.message === 'fetchEvents timeout') {
        console.warn('[useEvents] fetchEvents timeout - using cached data if available')
        // Don't clear existing events on timeout - keep what we have
      } else {
        setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
      }
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [user, userRole])

  useEffect(() => {
    // Fetch on mount - only if we haven't fetched yet AND we have both user and userRole
    // This prevents fetching when userRole is still null
    if (!hasFetchedRef.current && user && userRole) {
      hasFetchedRef.current = true
      fetchEvents()
    }
  }, [fetchEvents, user, userRole])

  // DON'T refetch when page becomes visible - data is already in state
  // This prevents unnecessary loading after tab switch
  // useEffect(() => {
  //   if (isVisible && hasFetchedRef.current && user && events.length === 0 && !isFetching) {
  //     fetchEvents()
  //   }
  // }, [isVisible, events.length, isFetching, user, fetchEvents])

  const createEvent = useCallback(async (
    eventData: Omit<Event, 'id' | 'created_at' | 'updated_at'>,
    syncWithGoogleCalendar: boolean = false
  ) => {
    try {
      const currentPlan = (subscription?.plan_type || 'free') as 'free' | 'starter' | 'professional' | 'enterprise'
      const nextPlan: 'starter' | 'professional' | 'enterprise' =
        currentPlan === 'free' ? 'starter' : currentPlan === 'starter' ? 'professional' : 'enterprise'

      // Check subscription limit before creating event
      const eventDate = new Date(eventData.event_date)
      const limitCheck = await canCreateEvent(eventDate)
      
      if (!limitCheck.allowed) {
        return { 
          data: null, 
          error: limitCheck.message || 'Osiągnięto limit wydarzeń dla Twojego planu',
          code: 'PLAN_LIMIT' as const,
          requiredPlan: nextPlan,
        }
      }

      const { data, error: createError } = await supabase
        .from('events')
        .insert([eventData] as unknown as never)
        .select(`
          *,
          clients (*),
          menu_packages (*)
        `)
        .single()

      if (createError) {
        return { data: null, error: createError.message }
      }

      // Sync with Google Calendar if requested and plan allows it
      if (syncWithGoogleCalendar && user && data && 'id' in data) {
        // Check if plan has Google Calendar access
        if (features?.googleCalendar) {
          try {
            await fetch('/api/calendar/google/sync', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: user.id,
                eventId: (data as { id: string }).id,
                action: 'create',
                event: data,
              }),
            })
          } catch (syncError) {
            console.error('Error syncing with Google Calendar:', syncError)
          }
        } else {
          console.warn('Google Calendar sync skipped - plan does not support it')
        }
      }

      // Refetch after create
      await fetchEvents()
      return { data, error: null }
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [user, fetchEvents, canCreateEvent, subscription])

  const updateEvent = useCallback(async (
    id: string,
    eventData: Partial<Event>,
    syncWithGoogleCalendar: boolean = false,
    calendarEventId?: string
  ) => {
    try {
      const { data, error: updateError } = await supabase
        .from('events')
        .update(eventData as unknown as never)
        .eq('id', id)
        .select(`
          *,
          clients (*),
          menu_packages (*)
        `)
        .single()

      if (updateError) {
        return { data: null, error: updateError.message }
      }

      // Sync with Google Calendar if requested and plan allows it
      if (syncWithGoogleCalendar && user && data && calendarEventId) {
        // Check if plan has Google Calendar access
        const planType = subscription?.plan_type || 'free'
        const hasGoogleCalendar = planType !== 'free'
        
        if (hasGoogleCalendar) {
          try {
            await fetch('/api/calendar/google/sync', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: user.id,
                eventId: id,
                action: 'update',
                calendarEventId,
                event: data,
              }),
            })
          } catch (syncError) {
            console.error('Error syncing with Google Calendar:', syncError)
          }
        } else {
          console.warn('Google Calendar sync skipped - plan does not support it')
        }
      }

      // Refetch after update
      await fetchEvents()
      return { data, error: null }
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [user, fetchEvents, subscription])

  const deleteEvent = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('events')
        .delete()
        .eq('id', id)

      if (deleteError) {
        return { error: deleteError.message }
      }

      // Refetch after delete
      await fetchEvents()
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [fetchEvents])

  // loading = true tylko gdy faktycznie fetchujemy i nie mamy danych
  // Ale tylko jeśli user jest zalogowany (query powinno się wykonać)
  const isLoading = !!user && isFetching && events.length === 0

  return {
    events,
    loading: isLoading,
    error,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
  }
}
